/**
 * Convert a single LineItemGroup to flat table format
 * @param {Object} lineItemGroup - A single LineItemGroup object
 * @param {Number} groupIndex - Index of this group
 * @returns {Object|null} { columns, rows, originalData, groupIndex } or null if invalid
 */
function convertSingleGroupToTable(lineItemGroup, groupIndex) {
  console.log(`[convertSingleGroupToTable] Processing group ${groupIndex}:`, lineItemGroup);
  
  // Defensive check
  if (!lineItemGroup || typeof lineItemGroup !== 'object') {
    console.error(`[convertSingleGroupToTable] Invalid group ${groupIndex} - not an object:`, lineItemGroup);
    return null;
  }
  
  // IMPORTANT: Don't process if this looks like a page wrapper (has 'page' and 'lineItemGroups')
  // This should have been unwrapped by convertLineItemsToTable() already
  if (lineItemGroup.page !== undefined && lineItemGroup.lineItemGroups !== undefined) {
    console.error(`[convertSingleGroupToTable] Received page-wrapped object, should be unwrapped first:`, lineItemGroup);
    return null;
  }
  
  // Check if LineItems exists and is an array
  if (!Array.isArray(lineItemGroup.LineItems)) {
    console.error(`[convertSingleGroupToTable] Group ${groupIndex} - LineItems is not an array:`, {
      hasLineItems: 'LineItems' in lineItemGroup,
      lineItemsType: typeof lineItemGroup.LineItems,
      allKeys: Object.keys(lineItemGroup)
    });
    return null;
  }
  
  if (lineItemGroup.LineItems.length === 0) {
    console.log(`[convertSingleGroupToTable] Group ${groupIndex} - No LineItems`);
    return null;
  }

  // Extract unique columns from ALL line items (not just first)
  const columnsMap = new Map();
  
  lineItemGroup.LineItems.forEach((lineItem) => {
    if (lineItem.LineItemExpenseFields) {
      lineItem.LineItemExpenseFields.forEach((field) => {
        if (field.LabelDetection?.Text) {
          const key = field.LabelDetection.Text;
          if (!columnsMap.has(key)) {
            columnsMap.set(key, {
              key: key,
              label: field.LabelDetection.Text,
              type: field.Type?.Text,
              originalField: field,
            });
          }
        }
      });
    }
  });

  const columns = Array.from(columnsMap.values());

  // Extract rows
  const rows = lineItemGroup.LineItems.map((lineItem, itemIndex) => {
    const row = {
      rowIndex: itemIndex,
      cells: {},
      originalLineItem: lineItem,
    };

    // First, populate cells from this line item's fields
    lineItem.LineItemExpenseFields.forEach((field) => {
      if (field.LabelDetection?.Text) {
        const fieldKey = field.LabelDetection.Text;
        row.cells[fieldKey] = {
          value: field.ValueDetection?.Text || '',
          confidence: field.ValueDetection?.Confidence || 0,
          geometry: field.ValueDetection?.Geometry || null,
          label: field.LabelDetection.Text,
          labelGeometry: field.LabelDetection?.Geometry || null,
          originalField: field,
          isModified: false,
        };
      }
    });

    // Then, ensure all columns have cells (even if empty for this row)
    columnsMap.forEach((column) => {
      if (!row.cells[column.key]) {
        row.cells[column.key] = {
          value: '',
          confidence: 0,
          geometry: null,
          label: column.label,
          labelGeometry: null,
          originalField: null,
          isModified: false,
        };
      }
    });

    return row;
  });

  return {
    columns,
    rows,
    originalData: lineItemGroup,
    groupIndex,
    pageNumber: undefined, // Will be set by convertLineItemsToTable if multi-page format
  };
}

/**
 * Convert LineItemGroups structure to flat table format for display
 * HANDLES BOTH:
 * - Old format: Array of LineItemGroup objects directly
 * - New multi-page format: Array of {page, lineItemGroups} with page metadata
 * @param {Array} lineItemGroups - Array of line item groups or page-wrapped groups from API
 * @returns {Array} Array of { columns, rows, originalData, groupIndex, pageNumber } objects
 */
export function convertLineItemsToTable(lineItemGroups) {
  console.log('[convertLineItemsToTable] Input:', lineItemGroups);
  
  // Handle the API response structure where the first element might have line_item_data property
  let actualLineItemGroups = lineItemGroups;
  
  // If the input is an array and the first element has line_item_data property
  if (Array.isArray(lineItemGroups) && lineItemGroups.length > 0 && lineItemGroups[0].line_item_data) {
    console.log('[convertLineItemsToTable] Detected API response format with line_item_data property');
    actualLineItemGroups = lineItemGroups[0].line_item_data;
    console.log('[convertLineItemsToTable] Extracted line_item_data:', actualLineItemGroups);
  }
  
  // Validate input is an array
  if (!Array.isArray(actualLineItemGroups)) {
    console.error('[convertLineItemsToTable] Data is not an array after extraction:', typeof actualLineItemGroups);
    return [];
  }
  
  if (actualLineItemGroups.length === 0) {
    console.log('[convertLineItemsToTable] Empty array provided');
    return [];
  }

  // Detect if this is the new multi-page format
  // Multi-page format has items with 'page' and 'lineItemGroups' properties
  const isMultiPageFormat = actualLineItemGroups.length > 0 && 
                            actualLineItemGroups[0].page !== undefined && 
                            actualLineItemGroups[0].lineItemGroups !== undefined;
  
  console.log('[convertLineItemsToTable] Detected multi-page format:', isMultiPageFormat);

  // Process all groups and collect valid tables
  const tables = [];
  
  if (isMultiPageFormat) {
    // NEW FORMAT: Array of {page, lineItemGroups}
    actualLineItemGroups.forEach((pageWrapper, pageIndex) => {
      const pageNumber = pageWrapper.page || (pageIndex + 1);
      const lineItemGroupsArray = pageWrapper.lineItemGroups;
      
      console.log(`[convertLineItemsToTable] Processing page ${pageNumber} with ${lineItemGroupsArray?.length || 0} line item groups`);
      
      if (Array.isArray(lineItemGroupsArray)) {
        lineItemGroupsArray.forEach((group, groupIndex) => {
          const table = convertSingleGroupToTable(group, groupIndex);
          if (table) {
            // Add page number to the table metadata for display purposes
            table.pageNumber = pageNumber;
            tables.push(table);
          }
        });
      }
    });
  } else {
    // OLD FORMAT: Direct array of LineItemGroup objects
    console.log('[convertLineItemsToTable] Using old format (direct LineItemGroup array)');
    actualLineItemGroups.forEach((group, index) => {
      const table = convertSingleGroupToTable(group, index);
      if (table) {
        tables.push(table);
      }
    });
  }

  console.log(`[convertLineItemsToTable] Processed ${actualLineItemGroups.length} groups, created ${tables.length} tables`);
  return tables;
}

/**
 * Convert a single edited table data back to original LineItemGroup structure
 * @param {Object} tableData - { columns, rows, originalData, groupIndex }
 * @param {Object} editedData - Tracking of what was edited
 * @returns {Object} Updated LineItemGroup object
 */
function convertSingleTableToLineItems(tableData, editedData = {}) {
  console.log('[convertSingleTableToLineItems] Processing table:', tableData);
  
  if (!tableData || !tableData.originalData) {
    console.error('[convertSingleTableToLineItems] No originalData found');
    return null;
  }

  const updatedGroup = JSON.parse(JSON.stringify(tableData.originalData)); // Deep clone

  // First pass: Update cell values for each row
  tableData.rows.forEach((row, rowIndex) => {
    const originalLineItem = updatedGroup.LineItems[rowIndex];
    if (!originalLineItem) return;

    originalLineItem.LineItemExpenseFields.forEach((field) => {
      const fieldKey = field.LabelDetection?.Text;
      if (fieldKey && row.cells[fieldKey]) {
        const cell = row.cells[fieldKey];
        
        // Update value detection if cell was modified
        if (cell.isModified || editedData[`${rowIndex}_${fieldKey}_value`]) {
          if (field.ValueDetection) {
            field.ValueDetection.Text = cell.value;
          }
        }
      }
    });
  });

  // Second pass: Update headers - apply to ALL fields with matching original label across ALL rows
  Object.keys(editedData).forEach((key) => {
    const originalLabel = key;
    const newLabel = editedData[key];
    
    // Update the label in all LineItems that have this field
    updatedGroup.LineItems.forEach((lineItem) => {
      lineItem.LineItemExpenseFields.forEach((field) => {
        // Match by original label text
        if (field.LabelDetection?.Text === originalLabel) {
          field.LabelDetection.Text = newLabel;
        }
      });
    });
  });

  return updatedGroup;
}

/**
 * Convert edited table data back to original LineItemGroups structure
 * @param {Array} tableDataArray - Array of { columns, rows, originalData, groupIndex } objects
 * @param {Object} editedDataMap - Map of groupIndex to editedData
 * @returns {Array} Updated LineItemGroups array
 */
export function convertTableToLineItems(tableDataArray, editedDataMap = {}) {
  console.log('[convertTableToLineItems] Input:', { tableDataArray, editedDataMap });
  
  // Handle single table object (backward compatibility)
  if (!Array.isArray(tableDataArray)) {
    console.warn('[convertTableToLineItems] Received single table object, converting to single-table update');
    const updatedGroup = convertSingleTableToLineItems(tableDataArray, editedDataMap);
    return updatedGroup ? [updatedGroup] : [];
  }

  // Process all tables
  const updatedGroups = [];
  tableDataArray.forEach((tableData, index) => {
    const editedData = editedDataMap[index] || editedDataMap[tableData.groupIndex] || {};
    const updatedGroup = convertSingleTableToLineItems(tableData, editedData);
    if (updatedGroup) {
      updatedGroups.push(updatedGroup);
    }
  });

  console.log('[convertTableToLineItems] Returning updated groups:', updatedGroups);
  return updatedGroups;
}

/**
 * Get geometry for highlighting from a cell
 * @param {Object} cell - Cell object with geometry data
 * @returns {Object} Geometry object or null
 */
export function getGeometryForCell(cell) {
  return cell?.geometry || null;
}

/**
 * Get geometry for highlighting from a column header
 * @param {Object} column - Column object with original field
 * @param {Array} rows - Array of row objects to get label geometry
 * @returns {Object} Geometry object or null
 */
export function getGeometryForHeader(column, rows) {
  // Get label geometry from first row that has this column
  if (!Array.isArray(rows)) return null;
  
  for (let row of rows) {
    if (row.cells && row.cells[column.key]) {
      return row.cells[column.key].labelGeometry || null;
    }
  }
  return null;
}

/**
 * Track field modification
 * @param {Object} row - Row object
 * @param {String} fieldKey - Field key
 */
export function markFieldAsModified(row, fieldKey) {
  if (row.cells[fieldKey]) {
    row.cells[fieldKey].isModified = true;
  }
}

/**
 * Track header modification
 * @param {String} fieldKey - Field key (column identifier)
 * @param {String} newLabel - New label value
 */
export function markHeaderAsModified(fieldKey, newLabel) {
  return {
    key: `header_${fieldKey}`,
    value: newLabel,
  };
}
